import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Input,
  InputNumber,
  Row,
  Select,
} from "antd";
import { FilterOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import { orderBy } from "lodash";
import Localize, { l } from "../../libs/localization";
import Form from "../Form/Form";
import Tree from "../Tree/Tree";
import Label from "../Label/Label";
import CompanyAutocomplete from "../CompanyAutocomplete/CompanyAutocomplete";
import PlaceAutocomplete from "../PlaceAutocomplete/PlaceAutocomplete";
import FinancialForm from "../../pages/form/FinancialForm";
import InfoForm from "../../pages/form/InfoForm";
import InvestmentForm from "../../pages/form/InvestmentForm";
import InvestmentRoundsForm from "../../pages/form/InvestmentRoundsForm";
import FlatV2Picker from "../FlatV2Picker";
import { translateRegistryOnView } from "../../libs/utils";
import useRegistryData from "../../hooks/useRegistryData";
import usePlacesData from "../../hooks/usePlacesData";
import { useSelector } from "react-redux";

const { Panel } = Collapse;

const CompanyFilters = ({
  dataForm,
  setDataForm,
  features = {},
  permissions = {},
  tenant,
  loadingSearch,
  brandAdvertiserFilters = [],
  setBrandAdvertiserFilters,
  onCompanyNameLabelsChange,
  isHR,
  renderExtraActions,
}) => {
  const [showOther, setShowOther] = useState(false);
  const [open, setOpen] = useState([1]);
  const [currentBrandAdvertiserFilter, setCurrentBrandAdvertiserFilter] =
    useState({
      platform: "",
      year: new Date().getFullYear(),
      property: "reach",
      surfaces: [],
      min: undefined,
      max: undefined,
    });

  const { mainCategory, sector, otherCategories } = useRegistryData();
  const { citiesList, regionsList, provincesList, loadPlaces } =
    usePlacesData();
  const token = useSelector((state) => state.auth.token);
  const language = useSelector((state) => state.app.language);

  useEffect(() => {
    if (showOther) {
      loadPlaces();
    }
  }, [showOther, loadPlaces]);

  const callback = (key) => {
    setOpen(key);
  };

  /*
   * Sort the sector tree recursively by the translated name, handling accents.
   */
  const sortedSectors = useMemo(() => {
    const sortTree = (tree) => {
      if (!tree || !Array.isArray(tree)) return [];
      const sorted = orderBy(
        tree,
        (item) => {
          const translated =
            translateRegistryOnView(item._id, "Sector", false, language) ||
            item.key;
          // Normalize to NFD and remove diacritics
          return translated
            ? translated
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase()
            : "";
        },
        ["asc"],
      );

      return sorted.map((item) => ({
        ...item,
        children: sortTree(item.children),
      }));
    };
    return sortTree(sector);
  }, [sector, language]);

  return (
    <div className="company-filters">
      <Form
        name="form-create"
        initialValues={dataForm}
        onChange={({ values, errors }) => {
          setDataForm({
            ...dataForm,
            ...values,
            ...errors,
          });
        }}
      >
        {({ updateValue }) => (
          <>
            <Row gutter={16}>
              <Col span={8}>
                <Label
                  className="filter-color"
                  value={Localize("PAGES.SEARCH.ORDER.NAME")}
                />
                <CompanyAutocomplete
                  token={token}
                  onChange={(value) => {
                    updateValue({
                      key: "id",
                      value: value.map((e) => e?.id),
                    });
                    if (typeof onCompanyNameLabelsChange === "function") {
                      const labels = {};
                      value.forEach((e) => {
                        if (e?.id && e?.name) labels[e.id] = e.name;
                      });
                      onCompanyNameLabelsChange(labels);
                    }
                  }}
                />
              </Col>
              <Col span={8}>
                <div>
                  <Label
                    className="filter-color"
                    value={Localize("PAGES.SEARCH.ORDER.CATEGORY")}
                  />
                  <Select
                    mode="multiple"
                    allowClear={true}
                    style={{ width: "100%" }}
                    value={dataForm.main_category}
                    onChange={(value) =>
                      updateValue({ key: "main_category", value })
                    }
                    showSearch
                    options={
                      mainCategory?.enum &&
                      orderBy(mainCategory.enum, ["id"], ["asc"])
                        .filter((o) => o.active)
                        .map((e) => ({
                          value: e.id,
                          label:
                            translateRegistryOnView(
                              e.id,
                              "main_category",
                              true,
                              language,
                            ) || e.id,
                          key: e.id,
                        }))
                    }
                  />
                </div>
              </Col>
              {features.comments && permissions.comments && (
                <Col span={8} className="mt-3">
                  <Row align="bottom" style={{ height: "100%" }}>
                    <Checkbox
                      checked={dataForm.commentsExists}
                      onChange={(value) => {
                        updateValue({
                          key: "commentsExists",
                          value: value.target.checked,
                        });
                      }}
                    >
                      <span className="filter-color">
                        {Localize("PAGES.SEARCH.ONLY_WITH_COMMENTS")}
                      </span>
                    </Checkbox>
                  </Row>
                </Col>
              )}
            </Row>
            <Row justify="space-between" align="bottom">
              <Col>
                <Button
                  type="link"
                  onClick={() => setShowOther(!showOther)}
                  className="secondary mt-3 pl-2"
                >
                  <FilterOutlined className="mr-1" />
                  {Localize(`COMMON.${!showOther ? "OTHER_FILTERS" : "CLOSE"}`)}
                </Button>
              </Col>
              <Col>{renderExtraActions && renderExtraActions()}</Col>
            </Row>
            {showOther && (
              <div
                className="animation fadein-right slow"
                style={{ marginTop: 20 }}
              >
                <Collapse onChange={callback} ghost={true} activeKey={open}>
                  <Panel
                    header={Localize("COMMON.COMPANY")}
                    key="1"
                    className="pt-3"
                  >
                    <Row gutter={16}>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={Localize("COMMON.CITY")}
                        />
                        <PlaceAutocomplete
                          disableOptions={loadingSearch}
                          data={citiesList?.sort().filter((el) => el !== "")}
                          value={dataForm.city}
                          onChange={(value) =>
                            updateValue({ key: "city", value })
                          }
                        />
                      </Col>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={Localize("COMMON.PROVINCE")}
                        />
                        <PlaceAutocomplete
                          disableOptions={loadingSearch}
                          data={provincesList?.sort().filter((el) => el !== "")}
                          value={dataForm.province}
                          onChange={(value) =>
                            updateValue({ key: "province", value })
                          }
                        />
                      </Col>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={Localize("COMMON.REGION")}
                        />
                        <PlaceAutocomplete
                          disableOptions={loadingSearch}
                          data={regionsList?.sort().filter((el) => el !== "")}
                          value={dataForm.region}
                          onChange={(value) =>
                            updateValue({ key: "region", value })
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={16} className="mt-3">
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={Localize("PAGES.SEARCH.ORDER.SECTOR")}
                        />
                        <Tree
                          translateEntity="Sector"
                          valueData={dataForm.sector}
                          datatree={sortedSectors}
                          onChange={(value) =>
                            updateValue({ key: "sector", value })
                          }
                          onSelect={(value) =>
                            updateValue({ key: "sector", value })
                          }
                          language={language}
                        />
                      </Col>
                      {tenant !== "spain" && (
                        <Col span={8}>
                          <Label
                            className="filter-color"
                            value={Localize("PAGES.SEARCH.ORDER.OTHER_CAT")}
                          />
                          <Select
                            mode="multiple"
                            allowClear={true}
                            style={{ width: "100%" }}
                            value={dataForm.other_categories}
                            onChange={(value) =>
                              updateValue({ key: "other_categories", value })
                            }
                            showSearch
                            options={
                              otherCategories?.set &&
                              orderBy(otherCategories.set, "id")
                                .filter((e) => e.id.length > 0)
                                .map((e) => ({
                                  value: e.id,
                                  label:
                                    translateRegistryOnView(
                                      e.id,
                                      "other_categories",
                                      true,
                                      language,
                                    ) || e.id,
                                  key: e.id,
                                }))
                            }
                          />
                        </Col>
                      )}
                      {features.atecoCode && (
                        <Col span={8}>
                          <Label
                            className="filter-color"
                            value={Localize("PAGES.SEARCH.ORDER.ATECO_CODE")}
                          />
                          <Input
                            value={dataForm.ateco_code}
                            onChange={(e) =>
                              updateValue({
                                key: "ateco_code",
                                value: e.target.value,
                              })
                            }
                          />
                        </Col>
                      )}
                    </Row>
                  </Panel>

                  {permissions.companyFinancials &&
                    features.companyFinancials && (
                      <Panel
                        header={Localize("PAGES.SEARCH.ORDER.FINANCIAL_DATA")}
                        key="2"
                        className="pt-3"
                      >
                        <FinancialForm
                          onChange={(key, value) => updateValue({ key, value })}
                          dataForm={dataForm}
                        />
                      </Panel>
                    )}

                  <Panel
                    header={Localize("PAGES.SEARCH.ORDER.ADDITION_INFO")}
                    key="3"
                    className="pt-3"
                  >
                    <InfoForm
                      onChange={(key, value) => updateValue({ key, value })}
                      dataForm={dataForm}
                      isHR={isHR}
                    />
                  </Panel>

                  {features.companyInvestments &&
                    permissions.companyInvestments && (
                      <Panel
                        header={Localize(
                          "PAGES.SEARCH.ORDER.ADEVERTISING_INVESTMENTS",
                        )}
                        key="4"
                        className="pt-3"
                      >
                        <InvestmentForm
                          onChange={(key, value) => updateValue({ key, value })}
                          dataForm={dataForm}
                        />
                      </Panel>
                    )}

                  <Panel
                    header={Localize("PAGES.SEARCH.SOCIAL_ADS")}
                    key="5"
                    className="pt-3"
                  >
                    {brandAdvertiserFilters.map((filter, index) => (
                      <Row gutter={16} key={index} style={{ marginBottom: 16 }}>
                        <Col span={4}>
                          <Label
                            className="filter-color"
                            value={Localize("BRAND_ADVERTISERS.PLATFORM")}
                          />
                          <div>{filter.platform || Localize("COMMON.ALL")}</div>
                        </Col>
                        <Col span={3}>
                          <Label
                            className="filter-color"
                            value={Localize("COMMON.YEAR")}
                          />
                          <div>{filter.year}</div>
                        </Col>
                        <Col span={3}>
                          <Label
                            className="filter-color"
                            value={Localize("PAGES.SEARCH.PROPERTY")}
                          />
                          <div>
                            {filter.property === "reach"
                              ? Localize("PAGES.SEARCH.REACH")
                              : Localize("BRAND_ADVERTISERS.N_ADS")}
                          </div>
                        </Col>
                        <Col span={4}>
                          <Label
                            className="filter-color"
                            value={Localize("COMMON.MIN")}
                          />
                          <div>{filter.min || "-"}</div>
                        </Col>
                        <Col span={4}>
                          <Label
                            className="filter-color"
                            value={Localize("COMMON.MAX")}
                          />
                          <div>{filter.max || "-"}</div>
                        </Col>
                        <Col span={5}>
                          <Button
                            type="default"
                            onClick={() =>
                              setBrandAdvertiserFilters(
                                brandAdvertiserFilters.filter(
                                  (_, i) => i !== index,
                                ),
                              )
                            }
                            style={{ marginTop: 24 }}
                          >
                            <MinusOutlined /> {Localize("COMMON.REMOVE")}
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                      <Col span={4}>
                        <Label
                          className="filter-color"
                          value={Localize("BRAND_ADVERTISERS.PLATFORM")}
                        />
                        <Select
                          allowClear
                          style={{ width: "100%" }}
                          value={
                            currentBrandAdvertiserFilter.platform || undefined
                          }
                          placeholder={Localize("COMMON.ALL")}
                          onChange={(value) =>
                            setCurrentBrandAdvertiserFilter({
                              ...currentBrandAdvertiserFilter,
                              platform: value || "",
                            })
                          }
                          options={[
                            {
                              value: "META",
                              label: Localize(
                                "BRAND_ADVERTISERS.PLATFORM_META",
                              ),
                            },
                            {
                              value: "GOOGLE",
                              label: Localize(
                                "BRAND_ADVERTISERS.PLATFORM_GOOGLE",
                              ),
                            },
                          ]}
                        />
                      </Col>
                      <Col span={3}>
                        <Label
                          className="filter-color"
                          value={Localize("COMMON.YEAR")}
                        />
                        <InputNumber
                          style={{ width: "100%" }}
                          min={2000}
                          max={new Date().getFullYear()}
                          value={currentBrandAdvertiserFilter.year}
                          onChange={(value) =>
                            setCurrentBrandAdvertiserFilter({
                              ...currentBrandAdvertiserFilter,
                              year: value,
                            })
                          }
                        />
                      </Col>
                      <Col span={3}>
                        <Label
                          className="filter-color"
                          value={Localize("PAGES.SEARCH.PROPERTY")}
                        />
                        <Select
                          style={{ width: "100%" }}
                          value={currentBrandAdvertiserFilter.property}
                          onChange={(value) =>
                            setCurrentBrandAdvertiserFilter({
                              ...currentBrandAdvertiserFilter,
                              property: value,
                            })
                          }
                          options={[
                            {
                              value: "reach",
                              label: Localize("PAGES.SEARCH.REACH"),
                            },
                            {
                              value: "adsCount",
                              label: Localize("BRAND_ADVERTISERS.N_ADS"),
                            },
                          ]}
                        />
                      </Col>
                      <Col span={4}>
                        <Label
                          className="filter-color"
                          value={Localize("COMMON.MIN")}
                        />
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          value={currentBrandAdvertiserFilter.min}
                          onChange={(value) =>
                            setCurrentBrandAdvertiserFilter({
                              ...currentBrandAdvertiserFilter,
                              min: value,
                            })
                          }
                        />
                      </Col>
                      <Col span={4}>
                        <Label
                          className="filter-color"
                          value={Localize("COMMON.MAX")}
                        />
                        <InputNumber
                          style={{ width: "100%" }}
                          min={1}
                          value={currentBrandAdvertiserFilter.max}
                          onChange={(value) =>
                            setCurrentBrandAdvertiserFilter({
                              ...currentBrandAdvertiserFilter,
                              max: value,
                            })
                          }
                        />
                      </Col>
                      <Col span={5}>
                        <Button
                          type="primary"
                          disabled={
                            !currentBrandAdvertiserFilter.year ||
                            (!currentBrandAdvertiserFilter.min &&
                              !currentBrandAdvertiserFilter.max)
                          }
                          onClick={() => {
                            setBrandAdvertiserFilters([
                              ...brandAdvertiserFilters,
                              { ...currentBrandAdvertiserFilter },
                            ]);
                            setCurrentBrandAdvertiserFilter({
                              platform: "",
                              year: new Date().getFullYear(),
                              property: "reach",
                              surfaces: [],
                              min: undefined,
                              max: undefined,
                            });
                          }}
                          style={{ marginTop: 24 }}
                        >
                          <PlusOutlined /> {Localize("COMMON.ADD")}
                        </Button>
                      </Col>
                    </Row>
                  </Panel>

                  {permissions.investmentRounds &&
                    features.investmentRounds && (
                      <Panel
                        header={Localize(
                          "PAGES.SEARCH.ORDER.INVESTMENT_ROUNDS",
                        )}
                        key="6"
                        className="pt-3"
                      >
                        <InvestmentRoundsForm
                          onChange={(key, value) => updateValue({ key, value })}
                          dataForm={dataForm}
                        />
                      </Panel>
                    )}
                </Collapse>
              </div>
            )}
          </>
        )}
      </Form>
    </div>
  );
};

export default CompanyFilters;
